import { Button, Colors, ConfirmationModal, DropdownSelect, Pagination, Typography, UserForResolver } from "@factorearth/component-library";
import { MultiToggleButton } from "components/MultiToggleButton/MultiToggleButton";
import { useTheme } from "hooks/useSingleton";
import styles from "./PhotoLog.module.scss";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiColumns, FiCopy, FiList, FiMinus, FiPlus, FiTrash2, FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import { FieldInstance, FieldInstanceForResolver, FieldTemplate, Image, RequirePropAsType } from "db/definitions";
import { PhotoLogCard } from "./PhotoLogCard/PhotoLogCard";
import { PhotoLogRow } from "./PhotoLogRow/PhotoLogRow";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import UploadFromPhotoLog from "./UploadFromPhotoLog/UploadFromPhotoLog";
import getTableFields from "utils/getTableFields";
import paginate from "utils/paginate";
import buildTableRow from "utils/buildTableRow";
import updateTable from "utils/updateTable";
import { handleBatchCrud } from "utils/handleBatchCrud";
import { bulkDeleteFieldInstances, bulkGetAndBuildFieldInstances, bulkSaveFieldInstances, createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import batchRemoveImageDocs from "utils/batchRemoveImageDocs";
import { bulkDeleteImageDocs, bulkSaveImageDocs, createImageForBatchResolver } from "db/queries/imageQueries";
import { bulkDeleteImageBinaryDocs } from "db/queries/imageBinaryQueries";
import deleteRows from "utils/deleteRows";
import { v4 as uuid } from "uuid";
import batchRetrieveFieldInstances from "utils/batchRetrieveFieldInstances";
import batchUploadFromPhotolog, { handleImageObjCopy } from "utils/batchUploadFromPhotolog";
import batchCreateFieldInstances from "utils/batchCreateFieldInstances";
import batchCreateImages from "utils/batchCreateImages";
import { recordSentry } from "utils/recordSentry";
import { getFieldsForDuplication } from "../TableField/tableFieldModules";
import verifyTableFields from "utils/verifyTableFields";
import { UpdateContext } from "utils/UpdateContext";
interface DeleteConfirmationModalProps {
	colorPalette: Colors;
	handleDelete: () => Promise<void>;
	loaded: boolean;
	rowKey: string;
	checkedRowsLength?: number;
	dataTestId?: string;
}

const DeleteConfirmationModal = ({ colorPalette, handleDelete, loaded, rowKey, checkedRowsLength, dataTestId }: DeleteConfirmationModalProps) => {
	const [open, setOpen] = useState(false);

	return (
		<>
			<Button
				icon={<FiTrash2 />}
				colorPalette={colorPalette}
				variant="destructive"
				onClick={() => setOpen(true)}
				disabled={!loaded || checkedRowsLength === 0}
				key={`delete-button-${rowKey}`}
				data-test={dataTestId}>
				Delete
			</Button>
			<ConfirmationModal
				colorPalette={colorPalette}
				setOpen={setOpen}
				details="This will permanently delete this image entry. Are you sure you want to continue?"
				confirmTitle="Delete Image Entry"
				handleSubmit={() => {
					handleDelete();
					setOpen(false);
				}}
				open={open}
				timeout={5000}
				canClose
			/>
		</>
	);
};

interface PhotoLogProps {
	updateObj?: FieldInstance;
	fieldInstance: FieldInstance;
	formGroupType: "default" | "photolog";
	projectId: string;
	fieldTemplate: FieldTemplate;
	userDoc: UserForResolver;
	online: boolean;
	setupDrawer: (location: Location | "") => void;
}

export interface RowObj {
	fields: FieldInstance[];
	image: RequirePropAsType<FieldInstance, "image", Image>;
	rowKey: string;
}

export const PhotoLog = (props: PhotoLogProps) => {
	const { updateObj, fieldInstance, formGroupType, projectId, fieldTemplate, userDoc, online, setupDrawer } = props;

	// STATE VARS
	const [initialFieldInstance, setInitialFieldInstance] = useState<FieldInstance>(fieldInstance);
	const [currentFieldInstance, setCurrentFieldInstance] = useState<FieldInstance>(fieldInstance);
	const [rowsData, setRowsData] = useState<FieldInstance[][]>([]);
	const [checkedRows, setCheckedRows] = useState<string[]>([]);

	// UI STATE VARS
	const [pageSize, setPageSize] = useState<{ label: string; value: string }[]>([{ label: "10", value: "10" }]);
	const [pageIndex, setPageIndex] = useState(1);
	const [sortBy, setSortBy] = useState<{ label: string; value: string } | undefined>(undefined);
	const [loaded, setLoaded] = useState(false);
	const [viewMode, setViewMode] = useState<string>("card");
	const [showUploadFromPhotolog, setShowUploadFromPhotolog] = useState(false);
	const [processInProgress, setProcessInProgress] = useState(false);

	const [scrollToRowKey, setScrollToRowKey] = useState<string | null>(null);

	const color = useTheme();
	const { linkedAltIds, linkedData } = useContext(UpdateContext);

	const headerMap: Record<string, FieldTemplate> = {};
	if (fieldTemplate.tableFields) {
		for (const tableCellTemplate of fieldTemplate.tableFields.items) {
			headerMap[tableCellTemplate.id] = tableCellTemplate;
		}
	}

	useEffect(() => {
		if (viewMode === "row") setPageSize([{ label: "10", value: "10" }]);
	}, [viewMode]);

	useEffect(() => {
		setUpRows();
	}, []);

	useEffect(() => {
		if (!updateObj) return;
		if (updateObj?.updatedAt > currentFieldInstance.updatedAt && currentFieldInstance.fieldsOrder) {
			if (updateObj?.forceUpdateData && updateObj.forceUpdateData !== currentFieldInstance.forceUpdateData) {
				// it has force data update and it is different than the existing, that means that it needs to do its thing
				const propToGrab = updateObj.forceUpdateData.split("-")[1];

				if (propToGrab && propToGrab === "tableFields") {
					setRowsData([]);
					refreshData(updateObj);
				}
			} else if (updateObj.fieldsOrder && updateObj.fieldsOrder.length > currentFieldInstance.fieldsOrder.length) {
				// it's a new row!
				refreshData(updateObj);
			} else if (updateObj.fieldsOrder && updateObj.fieldsOrder.length < currentFieldInstance.fieldsOrder.length) {
				// it's a deleted row!
				handleDeleteEvent(updateObj);
			} else if (updateObj && JSON.stringify(updateObj.fieldsOrder) !== JSON.stringify(currentFieldInstance.fieldsOrder)) {
				// rearrange row
				renderRearrange(updateObj);
			}

			setLoaded(true);
		}
	}, [updateObj, currentFieldInstance]);

	useEffect(() => {
		if (sortBy) sortRows(sortBy);
	}, [sortBy]);

	const scrollToNewRow = useCallback(
		(rowKey: string) => {
			// Small delay to ensure DOM is updated after state change
			setTimeout(() => {
				const element = document.querySelector(`[data-row-key="${rowKey}"]`) as HTMLElement;
				if (element) {
					element.scrollIntoView({
						behavior: "smooth",
						block: "center", // Centers the element in viewport
					});

					// Optional: Add a brief highlight animation
					element.style.transition = "background-color 0.5s ease";
					element.style.backgroundColor = color.background.tertiary || "#e3f2fd";
					setTimeout(() => {
						element.style.backgroundColor = "";
					}, 2000);
				}
			}, 10);
		},
		[color],
	);

	useEffect(() => {
		if (scrollToRowKey) {
			scrollToNewRow(scrollToRowKey);
			setScrollToRowKey(null);
		}
	}, [scrollToRowKey, scrollToNewRow]);

	async function refreshData(updateObj: FieldInstance) {
		setLoaded(false);

		const tableFieldRequestData = await getTableFields(fieldInstance.id, online);
		let newTableInstance: FieldInstance = { ...updateObj, tableFields: { items: tableFieldRequestData } };

		// Same repair as setUpRows: a subscription-driven refresh must not render an unverified row list. When nothing is
		// wrong this is a no-op (no writes).
		if (newTableInstance.fieldsOrder) {
			const verified = await verifyTableFields(fieldTemplate, newTableInstance.tableFields.items, newTableInstance, online, projectId);
			if (verified) newTableInstance = verified;
		}

		// Need to resetup rowsData at the same time
		const finalRowsData = setupRowsData(newTableInstance);
		setRowsData(finalRowsData);
		setCurrentFieldInstance(newTableInstance);
		setInitialFieldInstance(newTableInstance);
		setLoaded(true);
	}

	const renderRearrange = (update: FieldInstance) => {
		setLoaded(false);
		const newOrder = update.fieldsOrder || [];
		const finalData: FieldInstance[][] = [];
		const rowMap: Record<string, FieldInstance[]> = {};
		const newImageRollInstance = { ...currentFieldInstance, fieldsOrder: update.fieldsOrder };

		// Populate rowMap from existing rowsData using the current fieldsOrder as keys
		for (let i = 0; i < rowsData.length; i++) {
			const rowKey = currentFieldInstance.fieldsOrder?.[i];
			if (rowKey) {
				rowMap[rowKey] = rowsData[i];
			}
		}

		for (const rowOrder of newOrder) {
			const rowData = rowMap[rowOrder];
			if (rowData) {
				finalData.push(rowData);
			}
		}

		setLoaded(true);
		setRowsData(finalData);
		setCurrentFieldInstance(newImageRollInstance);
	};

	const setUpRows = async () => {
		setLoaded(false);

		// Fetch table fields from the DB in batches
		const tableFieldRequestData = await getTableFields(initialFieldInstance.id, online);
		const updatedInstance = updateObj || initialFieldInstance;
		let tableInstance = { ...updatedInstance, tableFields: { items: tableFieldRequestData } };

		// Check if the fieldsOrder is there (should always be there, this is mostly for TypeScript)
		if (!tableInstance.fieldsOrder) {
			console.error("fieldsOrder is missing in the tableInstance");
			return;
		}

		const verified = await verifyTableFields(fieldTemplate, tableInstance.tableFields.items, tableInstance, online, projectId);
		if (verified) tableInstance = verified;

		const finalRowsData = setupRowsData(tableInstance);
		// Set final data to state
		setRowsData(finalRowsData);
		setCurrentFieldInstance(tableInstance);
		setLoaded(true);
	};

	function setupRowsData(tableInstance: FieldInstance) {
		// Build a map of table fields by ID
		const fieldDataMap: Record<string, FieldInstance> = {};
		const tableFields = tableInstance.tableFields.items;
		for (const field of tableFields) {
			if (!field.fieldTemplateId) continue;
			const template = field.fieldTemplate || headerMap[field.fieldTemplateId];
			if (!field.fieldTemplate) {
				field.fieldTemplate = template;
			}
			fieldDataMap[field.id] = field;
		}

		// Build the table field rowData based off of rowsOrder for rowsData state
		const finalRowsData: FieldInstance[][] = [];
		for (const rowOrder of tableInstance.fieldsOrder!) {
			const rowIdsInOrder = rowOrder.split(",");
			const rowFields: FieldInstance[] = [];
			for (const id of rowIdsInOrder) {
				const field = fieldDataMap[id];
				if (!field) {
					console.error("Field not found! skipping... id: ", id);
					continue;
				}
				if (!field.fieldTemplate) continue;
				rowFields.push(field);
			}
			finalRowsData.push(rowFields);
		}
		return finalRowsData;
	}

	const addNewRow = async (insertAfterIndex?: number) => {
		setProcessInProgress(true);
		const insertIndex = insertAfterIndex !== undefined ? insertAfterIndex + 1 : rowsData.length;
		const copyOfFieldInstance = { ...currentFieldInstance, fieldsOrder: [...(currentFieldInstance.fieldsOrder ?? [])] };
		if (!copyOfFieldInstance.fieldsOrder) copyOfFieldInstance.fieldsOrder = [];
		const newRow = await buildTableRow(copyOfFieldInstance, headerMap, online, projectId, linkedAltIds, linkedData);

		let orderString = "";
		const newRowData: FieldInstance[] = [];

		for (let i = 0; i < newRow.length; i++) {
			const tableField = newRow[i];
			if (!tableField.fieldTemplateId || !tableField.fieldTemplate) continue;

			if (!tableField.fieldTemplate) {
				tableField.fieldTemplate = headerMap[tableField.fieldTemplateId];
			}

			orderString += i === newRow.length - 1 ? tableField.id : `${tableField.id},`;
			newRowData.push(tableField);
		}

		copyOfFieldInstance.fieldsOrder.splice(insertIndex, 0, orderString);
		const updatedRowsData = [...rowsData];
		updatedRowsData.splice(insertIndex, 0, newRowData);

		copyOfFieldInstance.tableFields.items = copyOfFieldInstance.tableFields.items.concat(newRow);
		copyOfFieldInstance.updatedAt = new Date().toISOString();

		setRowsData(updatedRowsData);
		setCurrentFieldInstance(copyOfFieldInstance);
		updateTable(copyOfFieldInstance, online);
		// Adjust Pagination
		updatePagination(updatedRowsData.length);
		const newRowPage = Math.ceil((insertIndex + 1) / Number(pageSize[0].value));
		setPageIndex(newRowPage);
		setProcessInProgress(false);
		if (orderString) {
			setScrollToRowKey(orderString);
		}
	};

	async function deleteMultipleRows(rowKeys?: string[]) {
		setProcessInProgress(true);
		setLoaded(false);
		const rowsToDelete = rowKeys ?? checkedRows;
		const newFieldsOrder = currentFieldInstance.fieldsOrder;
		const newRowsData = [...rowsData];
		const fieldsToDelete = [];
		for (const row of rowsData) {
			let rowString = "";
			for (const cell of row) {
				rowString = rowString ? (rowString += `,${cell.id}`) : cell.id;
			}
			if (rowsToDelete.indexOf(rowString) !== -1) {
				fieldsToDelete.push(...row);
			}
		}
		if (newFieldsOrder) {
			for (let i = 0; i < rowsToDelete.length; i++) {
				const rowToDelete = rowsToDelete[i];
				const fieldsOrderIndex = newFieldsOrder.indexOf(rowToDelete);

				newFieldsOrder.splice(fieldsOrderIndex, 1);
				newRowsData.splice(fieldsOrderIndex, 1);
			}
		}

		const uniqueFieldsToDelete = [...new Map(fieldsToDelete.map((f) => [f.id, f])).values()];
		const imageDocsToDelete = uniqueFieldsToDelete.map((x) => x.image).filter((img): img is Image => Boolean(img));
		if (currentFieldInstance) {
			const newFieldInstance: FieldInstance = {
				...currentFieldInstance,
				fieldsOrder: newFieldsOrder,
			};
			await Promise.all([updateTable(newFieldInstance, online), deleteRows(uniqueFieldsToDelete, online)]);
			Promise.all([
				online ? handleBatchCrud(imageDocsToDelete, batchRemoveImageDocs, 25) : bulkDeleteImageDocs(imageDocsToDelete.map((img) => img.id)),
				!online && bulkDeleteImageBinaryDocs(imageDocsToDelete.map((img) => (typeof img.key === "string" ? img.key : ""))),
			]);
			newFieldInstance.updatedAt = new Date().toISOString();
			setCurrentFieldInstance(newFieldInstance);
		}
		setRowsData(newRowsData);
		setCheckedRows([]);
		setLoaded(true);
		setProcessInProgress(false);
	}

	/**
	 * Updates the client based on a real time delete row event.
	 * @param newFieldInstance recieved from the event listener.
	 */
	function handleDeleteEvent(newFieldInstance: FieldInstance) {
		if (!newFieldInstance.fieldsOrder) return;
		setLoaded(false);
		const newData = [];

		for (let i = 0; i < rowsData.length; i++) {
			const row = rowsData[i];
			if (!row || row.length === 0) continue;
			if (newFieldInstance.fieldsOrder.indexOf(row.map((cell) => cell.id).join(",")) > -1) {
				// This row still exists - so push it in the new array
				newData.push(row);
			}
		}
		setRowsData(newData);
		setCurrentFieldInstance(newFieldInstance);
		setLoaded(true);
	}

	/**
	 * Takes the selected rows, duplicating the fields in each row. The duplicated rows appear beneath the selected row they were duplicated from
	 */
	async function duplicateMultipleRows(rowKeys?: string[]) {
		if (rowKeys && rowKeys.length > 1) {
			setLoaded(false);
		}
		const rowsToDuplicate = rowKeys ?? checkedRows;
		const isSingleRowDuplication = rowsToDuplicate.length === 1;

		if (isSingleRowDuplication) {
			setProcessInProgress(true);
		}
		const imageRollInstance = JSON.parse(JSON.stringify(currentFieldInstance));
		const newFieldsOrder = imageRollInstance.fieldsOrder;
		const rowsDataCopy = [...rowsData];
		const idToInstance = new Map();
		const idToData = new Map();
		const fieldsForBatchResolver = [];
		const fieldsForParentTableInState = [];
		const idsToBatchGet: string[] = rowsToDuplicate
			.join()
			.split(",")
			.filter((id) => id !== "");
		const retrievedFieldInstances = online
			? await handleBatchCrud(idsToBatchGet, batchRetrieveFieldInstances, 100)
			: await getFieldsForDuplication(idsToBatchGet);

		const newImages: Image[] = [];

		// map out the fields id: field
		for (let i = 0; i < retrievedFieldInstances.length; i++) {
			const field = retrievedFieldInstances[i];
			if (!field) {
				recordSentry({
					error: "Null field instance in batchRetrieveFieldInstances during row duplication",
					msg: `Expected field ID: ${idsToBatchGet[i]} was not found in DynamoDB`,
				});
				continue;
			}
			idToInstance.set(field.id, field);
		}

		let newRowKey: string | null = null;
		for (let i = 0; i < rowsToDuplicate.length; i++) {
			const rowCsv = rowsToDuplicate[i];
			const fieldIds = rowCsv.split(",");
			let fieldOrder = "";
			const dataRow = [];
			for (let j = 0; j < fieldIds.length; j++) {
				const idOfFieldToCopy = fieldIds[j];
				const fieldToCopy = idToInstance.get(idOfFieldToCopy);
				if (fieldToCopy) {
					const timestamp = new Date().toISOString();
					const id = uuid();
					const fieldCopy: FieldInstance = {
						...fieldToCopy,
						id,
						createdAt: timestamp,
						updatedAt: timestamp,
						tableFields: { items: [] },
					};

					if (fieldToCopy.imageId && fieldToCopy.image) {
						const newKey = await handleImageObjCopy(fieldToCopy.image, online);
						const duplicatedImage = createImageForBatchResolver(fieldToCopy.image);
						duplicatedImage.id = uuid();
						duplicatedImage.key = newKey;
						// Link the cloned image to the cloned field
						fieldCopy.imageId = duplicatedImage.id;
						fieldCopy.image = { ...duplicatedImage };

						newImages.push(duplicatedImage);
					}

					if (fieldCopy.fieldTemplateId) {
						const fieldForBatchResolver = createFieldForBatchResolver(fieldCopy);
						fieldsForBatchResolver.push(fieldForBatchResolver);
						dataRow.push(fieldCopy);
						fieldsForParentTableInState.push(fieldCopy);
						idToData.set(fieldCopy.id, fieldCopy);
						fieldOrder = fieldOrder ? (fieldOrder += `,${id}`) : id;
					}
				} else {
					// What do we do if we can't find the field? Shouldn't ever be an issue though
					console.error("Cannot find field id in array");
				}
			}
			if (!fieldOrder) {
				// All fields in this row were missing — skip inserting an empty row key to avoid corrupting fieldsOrder
				recordSentry({
					error: "duplicateMultipleRows produced empty fieldOrder",
					msg: `Row key '${rowCsv}' produced no valid field copies in PhotoLog id: ${fieldInstance.id}`,
				});
				continue;
			}
			const indexOfRowCsv = newFieldsOrder.indexOf(rowCsv);
			if (indexOfRowCsv !== -1) {
				newFieldsOrder.splice(indexOfRowCsv + 1, 0, fieldOrder);
				rowsDataCopy.splice(indexOfRowCsv + 1, 0, dataRow);
			} else {
				// wasn't found, so just add this to the end. Shouldn't ever happen though
				newFieldsOrder.push(fieldOrder);
				rowsDataCopy.push(dataRow);
			}
			// Capture the new row key for single row duplication
			if (isSingleRowDuplication && i === 0) {
				newRowKey = fieldOrder;
			}
		}
		const newTable: FieldInstance = {
			...imageRollInstance,
			fieldsOrder: newFieldsOrder,
			updatedAt: new Date().toISOString(),
			tableFields: {
				items: imageRollInstance.tableFields.items.concat(fieldsForParentTableInState),
			},
		};

		await Promise.all([
			updateTable(newTable, online),
			online ? handleBatchCrud(fieldsForBatchResolver, batchCreateFieldInstances, 25) : bulkSaveFieldInstances(fieldsForBatchResolver),
			online ? handleBatchCrud(newImages, batchCreateImages, 25) : bulkSaveImageDocs(newImages),
		]);

		setRowsData(rowsDataCopy);
		setCurrentFieldInstance(newTable);
		setCheckedRows([]);

		// Mirror addNewRow: keep pagination consistent and navigate to the new row
		updatePagination(rowsDataCopy.length);
		if (isSingleRowDuplication && newRowKey) {
			const newRowIndex = newTable.fieldsOrder ? newTable.fieldsOrder.indexOf(newRowKey) : -1;
			if (newRowIndex !== -1) {
				const newRowPage = Math.ceil((newRowIndex + 1) / Number(pageSize[0].value));
				setPageIndex(newRowPage);
			}
			setScrollToRowKey(newRowKey);
		}
		if (rowKeys && rowKeys.length > 1) {
			setLoaded(true);
		}
		if (isSingleRowDuplication) {
			setProcessInProgress(false);
		}
	}

	/**
	 * Method to pass in as a callback to .sort method.  Sorts based on the array that is
	 * passed into this.
	 * @param {array} array of the rows to base the sort on. Each element gets an i and value
	 * @param {object} valueMap of all the fields values to use to determine sort order.
	 * @param index of the field in the row to sort by.
	 * @param fieldType the input type of the field being sorted (e.g., "date", "text", etc.)
	 * @returns the function that sorts (pass this to .sort)
	 */
	function rowComparer(array: FieldInstance[][], valueMap: Record<string, string>, direction: 1 | -1, fieldType: string) {
		return function (rowA: number, rowB: number) {
			if (!array[rowA] || array[rowA].length < 1 || !array[rowB] || array[rowB].length < 1) {
				recordSentry({
					error: "Row has missing field instances",
					msg: `ImageRollField Row is missing field instances field instance id: ${fieldInstance.id}`,
				});
				return 0;
			}

			const aCellId = array[rowA].find((cell) => cell.id in valueMap)?.id;
			const bCellId = array[rowB].find((cell) => cell.id in valueMap)?.id;

			if (!aCellId || !bCellId) {
				recordSentry({
					error: "Row cell for sort column is missing",
					msg: `PhotoLog sort cell not found in valueMap for fieldInstance id: ${fieldInstance.id}`,
				});
				return 0;
			}

			const a = valueMap[aCellId],
				b = valueMap[bCellId];

			// Handle date fields with proper date comparison
			if (fieldType === "date") {
				const aDate = new Date(a);
				const bDate = new Date(b);
				const result = aDate.getTime() - bDate.getTime();
				return result * direction;
			}

			const aNum = parseInt(a, 10),
				bNum = parseInt(b, 10);
			const as = isNaN(aNum),
				bs = isNaN(bNum);
			if (as && bs) {
				// both are strings
				const result = a < b ? -1 : a === b ? 0 : 1;
				return result * direction;
			} else if (as) {
				// Just a is a string
				const result = 1;
				return result * direction;
			} else if (bs) {
				// just b is a string
				const result = -1;
				return result * direction;
			} else {
				// neither are strings
				const result = aNum - bNum;
				return result * direction;
			}
		};
	}

	const sortRows = async (e: { label: string; value: string }) => {
		setLoaded(false);
		const tableFieldsData = await getTableFields(currentFieldInstance.id, online);
		const valuesToSortByIdMap: Record<string, string> = {};
		// identify by what column (id) we are sorting by
		const valueInfo = e.value.split("_");
		// The template ID
		const idOfTemplateToSortBy = valueInfo[0];
		// "ASC" or "DES"
		const direction = valueInfo[1];
		// Column position
		const columnIndex = Number(valueInfo[2]);
		// The field inputType (e.g., "date", "text", etc.)
		const fieldType = valueInfo[3];
		// Build map of field ID -> current value for fields using this template
		for (let i = 0; i < tableFieldsData.length; i++) {
			const tableField = tableFieldsData[i];
			if (tableField.fieldTemplateId === idOfTemplateToSortBy) {
				valuesToSortByIdMap[tableField.id] = tableField.value;
			}
		}

		const indicesOfRows = [];
		for (let i = 0; i < rowsData.length; i++) {
			indicesOfRows.push(i);
		}

		const dir = direction === "ASC" ? 1 : -1;
		indicesOfRows.sort(rowComparer(rowsData, valuesToSortByIdMap, dir, fieldType));

		const newRowsData = [];
		const newFieldsOrder = [];
		for (const index of indicesOfRows) {
			newRowsData.push(rowsData[index]);
			if (currentFieldInstance.fieldsOrder) {
				newFieldsOrder.push(currentFieldInstance.fieldsOrder[index]);
			}
		}

		const sortedFieldInstance = { ...currentFieldInstance };
		sortedFieldInstance.fieldsOrder = newFieldsOrder;
		sortedFieldInstance.updatedAt = new Date().toISOString();

		setRowsData(newRowsData);
		setCurrentFieldInstance(sortedFieldInstance);
		setLoaded(true);
		updateTable(sortedFieldInstance, online);
	};

	const buildSortOptions = () => {
		const buildSortOptionByFieldType = (template: FieldTemplate, index: number) => {
			// Builds the possible options based off of the fields in a row - only text, number and date fields are supported.
			const ascending = { label: `${template.header} Ascending`, value: `${template.id}_ASC_${index}_${template.inputType}` };
			const descending = { label: `${template.header} Descending`, value: `${template.id}_DES_${index}_${template.inputType}` };
			const options = [ascending, descending];
			return options;
		};

		if (!fieldTemplate.rowOrder) return [];
		let final: { label: string; value: string }[] = [];
		for (let i = 0; i < fieldTemplate.rowOrder.length; i++) {
			const templateId = fieldTemplate.rowOrder[i];
			const template = headerMap[templateId];
			if (template.inputType === "image") {
				continue;
			}

			const options = buildSortOptionByFieldType(template, i);
			final = final.concat(options);
		}

		return final;
	};

	const updatePagination = (totalNumberOfRows: number) => {
		const pageSizeValue = pageSize.length ? Number(pageSize[0].value) : 1;
		const totalPages = totalNumberOfRows === 0 ? 1 : Math.ceil(totalNumberOfRows / pageSizeValue);

		if (pageIndex > totalPages) {
			setPageIndex(totalPages);
		}
	};

	async function handleSubmitTransfer(data: RowObj[]) {
		setLoaded(false);
		const newRowObjs = await batchUploadFromPhotolog(currentFieldInstance, data, online); // ⚠️ this isn't being used???
		setLoaded(true);
		// Reload page to view new rows added
		if (!online) window.location.reload();
	}

	const finalRows = useMemo(() => {
		const htmlOfRows = rowsData.map((rowData, rowIndex) => {
			let rowKey = currentFieldInstance?.fieldsOrder?.[rowIndex];
			if (!rowKey) {
				recordSentry({
					error: "Row key is undefined",
					msg: `Row key is undefined for row index: ${rowIndex} in PhotoLog component with fieldInstance id: ${fieldInstance.id}`,
				});
				rowKey = rowData.map((cell) => cell.id).join(",");
				return null; // Skip rendering this row
			}

			if (viewMode === "card") {
				const buttonsHtml = [
					processInProgress ? null : (
						<Button
							label="Add"
							colorPalette={color}
							onClick={() => addNewRow(rowIndex)}
							icon={<FiPlus />}
							variant="filled"
							id="photo-log-add-entry-button"
						/>
					),
					processInProgress ? (
						<div style={{ color: color.text.primary }}>Loading...</div>
					) : (
						<Button
							colorPalette={color}
							icon={<FiCopy />}
							variant="outlined"
							onClick={() => {
								duplicateMultipleRows([rowKey]);
							}}
							disabled={!loaded}
							key={`duplicate-button-${rowKey}`}>
							Duplicate
						</Button>
					),
					processInProgress ? null : (
						<DeleteConfirmationModal
							key={`delete-button-${rowKey}`}
							handleDelete={async () => {
								await deleteMultipleRows([rowKey]);
							}}
							rowKey={rowKey}
							colorPalette={color}
							loaded={loaded}
							dataTestId="photo-log-card-delete-button"
						/>
					),
				];

				return (
					<div data-row-key={rowKey} key={rowKey}>
						<PhotoLogCard
							key={rowKey}
							colorPalette={color}
							rowData={rowData}
							buttons={buttonsHtml}
							online={online}
							setupDrawer={setupDrawer}
							userDoc={userDoc}
						/>
					</div>
				);
			} else if (viewMode === "row") {
				return (
					<PhotoLogRow
						colorPalette={color}
						rowData={rowData}
						key={rowKey}
						checkedRows={checkedRows}
						setCheckedRows={setCheckedRows}
						rowId={rowKey}
						online={online}
						setupDrawer={setupDrawer}
						userDoc={userDoc}
					/>
				);
			} else {
				console.error("View mode not found");
			}
		});

		return paginate({ items: htmlOfRows, pageIndex, pageSize: pageSize.length ? Number(pageSize[0].value) : 1 });
	}, [color.border.secondary, rowsData, pageIndex, online, pageSize, viewMode, checkedRows, loaded, processInProgress]);

	return (
		<React.Fragment key={`ImageRoll-${initialFieldInstance.id}`}>
			<div data-fieldtemplateid={fieldTemplate.id} className={styles.logWrapper} style={{ borderBottom: `1px solid ${color.border.primary}` }}>
				<div>
					<div className={styles.sortOptionsLog}>
						{viewMode === "card" ? (
							<Button
								id="photo-log-add-entry-button"
								colorPalette={color}
								variant="outlined"
								label="Add"
								onClick={() => addNewRow(-1)}
								icon={<FiPlus />}
							/>
						) : (
							<Button
								id="photo-log-add-entry-button"
								colorPalette={color}
								variant="outlined"
								label="Add"
								onClick={() => addNewRow()}
								icon={<FiPlus />}
							/>
						)}
						{viewMode === "card" && (
							<div style={{ width: "400px", display: "flex", gap: "8px" }}>
								<DropdownSelect
									colorPalette={color}
									data-test="sort-by"
									name="sort-by"
									id="sort-by"
									onChange={(e: any) => {
										setSortBy(e);
									}}
									placeholder="Sort by field..."
									options={buildSortOptions()}
									isMulti={false}
									menuPortalTarget={document.body}
								/>
								<DropdownSelect
									// @ts-ignore
									colorPalette={color}
									data-test="photo-log-page-size"
									onChange={(e: any) => {
										setPageSize([e]);
										setPageIndex(1);
									}}
									options={[
										{ label: "1", value: "1" },
										{ label: "10", value: "10" },
										{ label: "25", value: "25" },
										{ label: "50", value: "50" },
										{ label: "75", value: "75" },
										{ label: "100", value: "100" },
									]}
									isMulti={false}
									menuPortalTarget={document.body}
									isSearchable={false}
									value={pageSize}
									id="page-size"
									name="page-size"
									menuPlacement="auto"
								/>
							</div>
						)}
						{viewMode === "row" && (
							<div className={styles.rowModeButtons}>
								<DeleteConfirmationModal
									key="row-mode-delete-btn"
									handleDelete={async () => {
										if (!loaded || checkedRows.length === 0) return;
										await deleteMultipleRows(checkedRows);
										setCheckedRows([]);
									}}
									rowKey={"multi-delete"}
									colorPalette={color}
									loaded={loaded}
									checkedRowsLength={checkedRows.length}
								/>
								<Button
									colorPalette={color}
									icon={<FiCopy />}
									variant="outlined"
									data-test="photo-log-duplicate-button"
									onClick={() => {
										// Duplicate selected rows
										if (!loaded || checkedRows.length === 0) return;
										duplicateMultipleRows(checkedRows);
									}}
									disabled={!loaded || checkedRows.length === 0}>
									Duplicate
								</Button>
							</div>
						)}
						<MultiToggleButton
							colorPalette={color}
							optionsObj={{
								card: {
									icon: <FiColumns />,
									title: "Cards",
									dataTestId: "photo-log-card-view-button",
								},
								row: {
									icon: <FiList />,
									title: "Rows",
									dataTestId: "photo-log-row-view-button",
								},
								// // this can be used once we are ready to add a gallery view to the photolog, type also needs to be added to viewMode state type
								// gallery: {
								// 	icon: <FiGrid />,
								// 	title: "Gallery",
								// }
							}}
							selected={viewMode}
							setSelected={setViewMode}
						/>
						{formGroupType === "default" && (
							<Button
								variant="filled"
								colorPalette={color}
								onClick={() => setShowUploadFromPhotolog(!showUploadFromPhotolog)}
								label="Add Row From Photo Log"
							/>
						)}
					</div>
					<div className={styles.pagination_nation}>
						<Pagination
							currentPage={pageIndex}
							setCurrentPage={setPageIndex}
							totalPages={pageSize.length ? Math.ceil(rowsData.length / Number(pageSize[0].value)) : 1}
							colorPalette={color}
						/>
					</div>
				</div>
			</div>
			<div className={styles.photologInputs}>
				{!loaded ? (
					<LoadingSpinner />
				) : !showUploadFromPhotolog ? (
					<table className={styles.table}>
						{viewMode === "row" && (
							<thead className={styles.tableHead} style={{ background: color.background.primary }}>
								<tr
									className={styles.tableRow}
									style={{
										background: color.background.primary,
										borderBottom: `${color.border.primary} 0.5px solid`,
									}}>
									<th className={styles.tableHeaderCell} style={{ borderBottom: `${color.border.primary} 0.5px solid` }}>
										<input
											type="checkbox"
											id="check-all"
											checked={
												checkedRows.length ===
													rowsData
														.map((_, idx) => {
															const rowKey = currentFieldInstance?.fieldsOrder?.[idx];
															return rowKey || "";
														})
														.filter((key) => key !== "").length &&
												rowsData
													.map((_, idx) => {
														const rowKey = currentFieldInstance?.fieldsOrder?.[idx];
														return rowKey || "";
													})
													.filter((key) => key !== "").length > 0
											}
											onChange={(e) => {
												if (e.target.checked) {
													setCheckedRows(
														rowsData
															.map((_, idx) => {
																const rowKey = currentFieldInstance?.fieldsOrder?.[idx];
																return rowKey || "";
															})
															.filter((key) => key !== ""),
													);
												} else {
													setCheckedRows([]);
												}
											}}
											className={styles.styledCheckbox}
										/>
									</th>

									{initialFieldInstance.fieldTemplate?.rowOrder &&
										initialFieldInstance.fieldTemplate?.rowOrder.map((fieldTemplateId, idx) => {
											const header = headerMap[fieldTemplateId]?.header || "No Header";
											return (
												<th
													className={styles.tableHeaderCell}
													style={{ borderBottom: `${color.border.primary} 0.5px solid` }}
													key={idx}>
													<div className={styles.tableHeaderCellContent}>
														<Typography variant={"Heading 5"} textColor={color.text.primary}>
															{header === "Image Field" ? "Image" : header}
														</Typography>
														{header !== "Image Field" && (
															<SortMenu
																colorPalette={color}
																setSortBy={setSortBy}
																template={headerMap[fieldTemplateId]}
																idx={idx}
															/>
														)}
													</div>
												</th>
											);
										})}
								</tr>
							</thead>
						)}
						{finalRows}
					</table>
				) : (
					<UploadFromPhotoLog
						cancel={() => setShowUploadFromPhotolog(false)}
						cb={(data: RowObj[]) => handleSubmitTransfer(data)}
						imageRollInstance={currentFieldInstance}
						online={online}
						projectId={projectId}
						template={currentFieldInstance.fieldTemplate}
					/>
				)}
			</div>
			<div className={styles.pagination_nation}>
				<Pagination
					currentPage={pageIndex}
					setCurrentPage={setPageIndex}
					totalPages={pageSize.length ? Math.ceil(rowsData.length / Number(pageSize[0].value)) : 1}
					colorPalette={color}
				/>
			</div>
		</React.Fragment>
	);
};

interface SortMenuProps {
	colorPalette: Colors;
	setSortBy: React.Dispatch<React.SetStateAction<{ label: string; value: string } | undefined>>;
	template: any;
	idx: number;
}

const SortMenu = (props: SortMenuProps) => {
	const { colorPalette, setSortBy, template, idx } = props;

	const [open, setOpen] = useState<boolean>(false);
	const menuRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		if (open) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]);

	return (
		<div ref={menuRef} className={styles.sortByMenu}>
			{open ? (
				<FiChevronUp onClick={() => setOpen(false)} style={{ cursor: "pointer", color: colorPalette.text.primary }} />
			) : (
				<FiChevronDown onClick={() => setOpen(true)} style={{ cursor: "pointer", color: colorPalette.text.primary }} />
			)}
			{open && (
				<div
					className={styles.sortByMenuOpen}
					style={{
						backgroundColor: colorPalette.background.primary,
						border: `1px solid ${colorPalette.border.primary}`,
						boxShadow: `0 4px 12px ${colorPalette.background.secondary}`,
					}}>
					<div className={styles.menuItemStyle} style={{ color: colorPalette.text.tertiary }} onClick={() => setSortBy(undefined)}>
						<FiMinus /> None
					</div>
					<div
						className={styles.menuItemStyle}
						style={{ color: colorPalette.text.tertiary }}
						onClick={() => setSortBy({ label: `${template.header} Ascending`, value: `${template.id}_ASC_${idx}_${template.inputType}` })}>
						<FiTrendingUp /> Ascending
					</div>
					<div
						className={styles.menuItemStyle}
						style={{ color: colorPalette.text.tertiary }}
						onClick={() => setSortBy({ label: `${template.header} Descending`, value: `${template.id}_DES_${idx}_${template.inputType}` })}>
						<FiTrendingDown /> Descending
					</div>
				</div>
			)}
		</div>
	);
};
